// Client-side instrumentation. Next runs this after the HTML loads but before
// React hydration (see node_modules/next/.../instrumentation-client.md) — the
// earliest safe point to boot monitoring so we also capture failures that happen
// during hydration itself. Kept lightweight; Next warns if init exceeds 16ms.
import Bugsnag from "@bugsnag/js"
import BugsnagPerformance from "@bugsnag/browser-performance"
import BugsnagPluginReact from "@bugsnag/plugin-react"
import React from "react"

import {
  API_KEY,
  APP_VERSION,
  type BugsnagErrorBoundary,
  ENABLED_RELEASE_STAGES,
  leaveBreadcrumb,
  REDACTED_KEYS,
  RELEASE_STAGE,
  registerClient,
  registerErrorBoundary,
} from "@/lib/observability/bugsnag"

// No key configured (e.g. local dev) → stay entirely dormant.
if (API_KEY) {
  const client = Bugsnag.start({
    apiKey: API_KEY,
    appVersion: APP_VERSION,
    releaseStage: RELEASE_STAGE,
    enabledReleaseStages: ENABLED_RELEASE_STAGES,
    plugins: [new BugsnagPluginReact()],
    // We only ever attach member.id — never collect IP alongside it.
    collectUserIp: false,
    redactedKeys: REDACTED_KEYS,
  })
  registerClient(client)

  const reactPlugin = client.getPlugin("react")
  if (reactPlugin) {
    registerErrorBoundary(
      reactPlugin.createErrorBoundary(React) as BugsnagErrorBoundary
    )
  }

  // Performance: auto-instruments full page loads (Core Web Vitals), SPA route
  // changes, and fetch/XHR requests → otlp.bugsnag.com/v1/traces.
  BugsnagPerformance.start({
    apiKey: API_KEY,
    appVersion: APP_VERSION,
    releaseStage: RELEASE_STAGE,
    enabledReleaseStages: ENABLED_RELEASE_STAGES,
  })
}

// Called by Next on every client-side router transition. Drops a breadcrumb so
// error reports carry the route trail that led to a failure.
export function onRouterTransitionStart(url: string) {
  leaveBreadcrumb("Route change", { to: url }, "navigation")
}
